import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  auth, 
  signInWithGoogle, 
  signInWithTwitter, 
  signInWithGithub, 
  signInWithMetamask,
  logOut,
  getAuth, 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  TwitterAuthProvider, 
  GithubAuthProvider, 
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from '../services/firebase';
import { User as FirebaseUser } from 'firebase/auth';

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  loginWithTwitter: () => Promise<void>;
  loginWithGithub: () => Promise<void>;
  loginWithMetamask: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  loginWithGoogle: async () => {},
  loginWithTwitter: async () => {},
  loginWithGithub: async () => {},
  loginWithMetamask: async () => {},
  loginWithEmail: async () => {},
  registerWithEmail: async () => {},
  resetPassword: async () => {},
  logout: async () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const loginWithTwitter = async () => {
    try {
      await signInWithTwitter();
    } catch (error) {
      console.error('Twitter login error:', error);
      throw error;
    }
  };

  const loginWithGithub = async () => {
    try {
      await signInWithGithub();
    } catch (error) {
      console.error('Github login error:', error);
      throw error;
    }
  };

  const loginWithMetamask = async () => {
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask extension is not installed. Please install MetaMask to use this feature.');
      }
      
      await signInWithMetamask();
    } catch (error) {
      console.error('Metamask login error:', error);
      // Provide a more user-friendly error message based on the error
      if (error instanceof Error) {
        if (error.message.includes('MetaMask is not installed')) {
          throw new Error('MetaMask extension is not installed. Please install MetaMask to use this feature.');
        } else if (error.message.includes('User denied')) {
          throw new Error('You need to approve the signature request in MetaMask to proceed.');
        } else if (error.message.includes('Backend')) {
          throw new Error('There was an issue connecting to our authentication server. Please try again later.');
        }
      }
      throw error;
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    const auth = getAuth();
    await signInWithEmailAndPassword(auth, email, password);
  };

  const registerWithEmail = async (email: string, password: string) => {
    const auth = getAuth();
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const resetPassword = async (email: string) => {
    const auth = getAuth();
    await sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    const auth = getAuth();
    await signOut(auth);
  };

  const value = {
    user,
    loading,
    loginWithGoogle,
    loginWithTwitter,
    loginWithGithub,
    loginWithMetamask,
    loginWithEmail,
    registerWithEmail,
    resetPassword,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 